<template>
    <InformationPage
        title="FAQ"
        description="Read the frequently asked questions about our service."
        :breadcrumb="[
            { icon: 'i-heroicons-home', label: 'Home', to: '/' },
            { label: 'FAQ' }
        ]"
    >
      <div class="space-y-8">
        <div v-for="group in faqGroups" :key="group.title" class="space-y-4">
          <h2 class="text-2xl font-bold">{{ group.title }}</h2>
          <UAccordion :items="group.items" />
        </div>
      </div>
    <template #aside>
            <UCard variant="outline" color="neutral" class="w-full" title="Need more help?">
                <template #header>
                    <h3 class="text-lg font-semibold">Need more help?</h3>
                </template>
                <div>If you can't find the answer you're looking for, our support team is here to help.</div>
                <template #footer>
                    <UButton trailing-icon="i-heroicons-arrow-right" href="/contact" color="primary">
                        Contact Support
                    </UButton>
                </template>
            </UCard>
            <UCard variant="outline" color="neutral" class="w-full" title="Helpful Resources">
                <template #header>
                    <h3 class="text-lg font-semibold">Helpful Resources</h3>
                </template>
                <ul class="space-y-2">
                    <li>
                        <ULink href="/shipping-info" color="primary">Shipping Information</ULink>
                    </li>
                    <li>
                        <ULink href="/return-policy" color="primary">Return Policy</ULink>
                    </li>
                    <li>
                        <ULink href="/payment-options" color="primary">Payment Options</ULink>
                    </li>
                    <li>
                        <ULink href="/account-management" color="primary">Account Management</ULink>
                    </li>
                </ul>
            </UCard>
    </template>
</InformationPage>
</template>
<script setup lang="ts">
import InformationPage from '~/components/templates/InformationPage.vue';

const faqGroups = ref([
  {
    title: 'Orders & Shipping',
    items: [
      {
        label: 'How long does shipping take?',
        content: 'Standard shipping typically takes 3-7 business days within the continental US. Express shipping (2-3 business days) and overnight shipping options are also available at checkout. International shipping times vary by destination, typically 7-14 business days.'
      },
      {
        label: 'Do you offer free shipping?',
        content: 'Yes! We offer free standard shipping on all orders over $50 within the US. Free international shipping is available on orders over $100. Shipping costs for orders below these thresholds are calculated based on weight and destination at checkout.'
      },
      {
        label: 'Can I track my order?',
        content: 'Absolutely! Once your order ships, you\'ll receive a tracking number via email. You can also track your order status by logging into your account and visiting the Order History page. Tracking information is updated in real-time.'
      },
      {
        label: 'Do you ship internationally?',
        content: 'Yes, we ship to most countries worldwide. International shipping rates and delivery times vary by destination. Please note that customers are responsible for any customs fees, duties, or taxes imposed by their country.'
      },
      {
        label: 'What should I do if my order arrives damaged?',
        content: 'We\'re sorry if your order arrived damaged! Please contact our customer service team within 48 hours of delivery with photos of the damaged item and packaging. We\'ll arrange for a replacement or full refund immediately.'
      }
    ]
  },
  {
    title: 'Returns & Refunds',
    items: [
      {
        label: 'What is your return policy?',
        content: 'We offer a 30-day return policy for all unused items in their original condition and packaging. Simply initiate a return through your account, print the prepaid return label, and send the item back. Refunds are processed within 5-7 business days of receiving your return.'
      },
      {
        label: 'How do I return an item?',
        content: 'Log into your account, go to Order History, select the order containing the item you wish to return, and click "Return Item". Choose your reason for the return, and we\'ll email you a prepaid return shipping label. Pack the item securely and drop it off at any carrier location.'
      },
      {
        label: 'Are there any items that cannot be returned?',
        content: 'For hygiene reasons, we cannot accept returns on opened personal care items, underwear, or customized/personalized products. Sale items marked as "Final Sale" are also non-returnable. All other items can be returned within 30 days.'
      },
      {
        label: 'How long does it take to receive a refund?',
        content: 'Once we receive your return, we\'ll inspect it and process your refund within 5-7 business days. The refund will be issued to your original payment method. Please allow an additional 3-5 business days for the refund to appear in your account, depending on your financial institution.'
      },
      {
        label: 'Can I exchange an item instead of returning it?',
        content: 'Yes! If you need a different size or color, you can request an exchange through your account. We\'ll send the replacement item as soon as we receive your return. Exchange shipping is free for orders over $50.'
      }
    ]
  },
  {
    title: 'Payment & Pricing',
    items: [
      {
        label: 'What payment methods do you accept?',
        content: 'We accept all major credit cards (Visa, MasterCard, American Express, Discover), PayPal, Apple Pay, Google Pay, and Shop Pay. All transactions are securely processed and encrypted for your protection.'
      },
      {
        label: 'Is it safe to use my credit card on your site?',
        content: 'Absolutely! We use industry-standard SSL encryption to protect your personal and payment information. We never store your full credit card details on our servers. All payment processing is handled by certified, PCI-compliant payment processors.'
      },
      {
        label: 'Do you offer price matching?',
        content: 'Yes! If you find an identical item at a lower price from an authorized retailer, we\'ll match that price. Simply contact our customer service team with the competitor\'s URL and pricing within 7 days of your purchase.'
      },
      {
        label: 'Can I use multiple discount codes?',
        content: 'Only one discount code can be applied per order. If you have multiple codes, we recommend using the one that provides the greatest discount. Discount codes cannot be combined with sale prices unless specifically stated.'
      },
      {
        label: 'Do you offer student or military discounts?',
        content: 'Yes! We offer a 15% discount for students and active military personnel. Verify your status through our partner verification service at checkout to receive your discount code.'
      }
    ]
  },
  {
    title: 'Products & Availability',
    items: [
      {
        label: 'How do I know if an item is in stock?',
        content: 'Stock availability is displayed on each product page. If an item is out of stock, you\'ll see an "Out of Stock" label. You can sign up for restock notifications by entering your email address on the product page, and we\'ll notify you when it\'s available again.'
      },
      {
        label: 'Can I pre-order items that are out of stock?',
        content: 'Pre-orders are available for select upcoming products. When a product is available for pre-order, you\'ll see a "Pre-Order" button instead of "Add to Cart". Pre-orders are charged at the time of purchase and ship as soon as the item becomes available.'
      },
      {
        label: 'Do you restock popular items?',
        content: 'Yes! We regularly restock popular items. If something is currently out of stock, sign up for restock notifications on the product page. Most items are restocked within 2-4 weeks, though availability depends on our suppliers.'
      },
      {
        label: 'Are your product photos accurate?',
        content: 'We strive to provide accurate product photos and descriptions. However, please note that colors may vary slightly due to screen settings and lighting conditions. Each product page includes detailed specifications to help you make an informed decision.'
      },
      {
        label: 'Do you offer gift wrapping?',
        content: 'Yes! Gift wrapping is available for $5 per item at checkout. We\'ll beautifully wrap your item and include a personalized gift message. Gift receipts (without pricing) are included automatically with gift-wrapped orders.'
      }
    ]
  },
  {
    title: 'Account & Privacy',
    items: [
      {
        label: 'Do I need an account to place an order?',
        content: 'No, you can checkout as a guest. However, creating an account allows you to track orders, save addresses, view order history, save items to your wishlist, and checkout faster on future purchases.'
      },
      {
        label: 'How do I reset my password?',
        content: 'Click "Sign In" at the top of any page, then click "Forgot Password?". Enter your email address and we\'ll send you a password reset link. Follow the instructions in the email to create a new password.'
      },
      {
        label: 'Is my personal information secure?',
        content: 'Yes! We take your privacy seriously. Your personal information is encrypted and stored securely. We never sell or share your information with third parties for marketing purposes. Read our Privacy Policy for complete details on how we protect your data.'
      },
      {
        label: 'How do I update my account information?',
        content: 'Log into your account and click on "Account Settings". From there, you can update your name, email address, password, shipping addresses, and communication preferences at any time.'
      },
      {
        label: 'Can I delete my account?',
        content: 'Yes, you can request account deletion by contacting our customer service team. Please note that deleting your account will permanently remove your order history, saved addresses, and wishlist. This action cannot be undone.'
      }
    ]
  },
  {
    title: 'Customer Service',
    items: [
      {
        label: 'How can I contact customer service?',
        content: 'You can reach our customer service team via email at support@trivian.com, by phone at 1-800-TRIVIAN (Mon-Fri 9AM-6PM EST), or through our live chat feature available on every page. We typically respond to emails within 24 hours.'
      },
      {
        label: 'Do you have a physical store?',
        content: 'We are an online-only retailer, which allows us to offer competitive prices and a wider selection. However, we occasionally participate in pop-up events and trade shows. Follow us on social media to stay updated on in-person shopping opportunities.'
      },
      {
        label: 'Do you offer bulk or wholesale pricing?',
        content: 'Yes! For bulk orders (typically 20+ items) or wholesale inquiries, please contact our B2B sales team at wholesale@trivian.com. We offer special pricing for businesses, resellers, and large quantity purchases.'
      }
    ]
  }
])

definePageMeta({
    layout: 'information',
})
</script>